// Tiny streaming-friendly Markdown renderer. Handles the subset LLM
// output actually produces: paragraphs, headings (#–######), fenced
// + inline code, bold (** **), italic (* *), links, bullet/ordered
// lists, blockquotes. Forgiving with partial input so a mid-stream
// message stays readable: an unclosed code fence renders the rest as
// code; an unmatched `**` falls through as literal text.
//
// Cost model: parse + render is O(n) per text update. With memoization
// on `text`, re-parsing only happens when text changes; React's
// reconciler handles the rest. For chat-sized payloads this is well
// under a frame and avoids pulling in a 30 kB markdown lib.

import { memo, useCallback, useMemo } from "react";
import DataTable from "./DataTable.jsx";
import ExportableBlock from "./ExportableBlock.jsx";
import SafeRenderLink from "./SafeRenderLink.jsx";
import { useDataTableColumns } from "../lib/columnSelection.js";
import { sanitizeRenderHref } from "../lib/renderSafety.js";

const HEADING_RE = /^(#{1,6})\s+(.+)$/;
const BULLET_RE = /^([-*+])\s+(.+)$/;
const ORDERED_RE = /^(\d+)\.\s+(.+)$/;
const QUOTE_RE = /^>\s?(.*)$/;
// An indented line that is no list item of its own nor a code fence.
const CONTINUES_RE = /^\s+(?![-*+]\s|\d+\.\s|```)\S/;
// A thematic break: three or more of one of - * _ alone on a line, spaces
// allowed between them (`---`, `* * *`). Checked before lists, which `* * *`
// would otherwise open.
const RULE_RE = /^ {0,3}([-*_])(?: *\1){2,} *$/;

// GFM table detection. A row is anything pipe-delimited with content;
// the separator is the line of dashes immediately under the header
// (with optional `:` for alignment, e.g. `| :--- | :---: | ---: |`).
const TABLE_ROW_RE = /^\s*\|.*\|\s*$/;
const TABLE_SEP_RE = /^\s*\|(?:\s*:?-+:?\s*\|)+\s*$/;

function splitTableRow(line) {
    const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
    return trimmed.split("|").map((c) => c.trim());
}

function parseAlignments(sepLine) {
    return splitTableRow(sepLine).map((cell) => {
        const left = cell.startsWith(":");
        const right = cell.endsWith(":");
        if (left && right) return "center";
        if (right) return "right";
        if (left) return "left";
        return null;
    });
}

function parseBlocks(text) {
    const lines = text.split("\n");
    const blocks = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        if (line.startsWith("```")) {
            const lang = line.slice(3).trim();
            const buf = [];
            i++;
            while (i < lines.length && !lines[i].startsWith("```")) {
                buf.push(lines[i]);
                i++;
            }
            // Skip closing fence if present; partial streams may not have one yet.
            if (i < lines.length) i++;
            blocks.push({ type: "code", lang, content: buf.join("\n") });
            continue;
        }
        if (RULE_RE.test(line)) {
            blocks.push({ type: "rule" });
            i++;
            continue;
        }
        const heading = HEADING_RE.exec(line);
        if (heading) {
            blocks.push({ type: "heading", level: heading[1].length, content: heading[2] });
            i++;
            continue;
        }
        // GFM table: header row + separator row, then any number of
        // body rows. Detected by looking ahead one line to confirm
        // the separator before committing — otherwise a stray pipe in
        // prose would steal the surrounding text.
        if (
            TABLE_ROW_RE.test(line) &&
            i + 1 < lines.length &&
            TABLE_SEP_RE.test(lines[i + 1])
        ) {
            const header = splitTableRow(line);
            const aligns = parseAlignments(lines[i + 1]);
            i += 2;
            const rows = [];
            while (i < lines.length && TABLE_ROW_RE.test(lines[i])) {
                rows.push(splitTableRow(lines[i]));
                i++;
            }
            blocks.push({ type: "table", header, rows, aligns });
            continue;
        }
        if (BULLET_RE.test(line) || ORDERED_RE.test(line)) {
            const ordered = ORDERED_RE.test(line);
            const re = ordered ? ORDERED_RE : BULLET_RE;
            // Numbered from its first item, as the model wrote it.
            const start = ordered ? Number(ORDERED_RE.exec(line)[1]) : 1;
            const items = [];
            while (i < lines.length) {
                const item = re.exec(lines[i]);
                if (item) {
                    items.push(item[2]);
                    i++;
                    continue;
                }
                // A line indented under an item carries that item on.
                if (CONTINUES_RE.test(lines[i])) {
                    items[items.length - 1] += ` ${lines[i].trim()}`;
                    i++;
                    continue;
                }
                // Items with blank lines between them are still one list,
                // numbered on, not a list of one each.
                if (lines[i].trim() === "") {
                    let next = i;
                    while (next < lines.length && lines[next].trim() === "") next++;
                    if (next < lines.length && re.test(lines[next])) {
                        i = next;
                        continue;
                    }
                }
                break;
            }
            blocks.push({ type: "list", ordered, start, items });
            continue;
        }
        if (QUOTE_RE.test(line)) {
            const buf = [];
            while (i < lines.length && QUOTE_RE.test(lines[i])) {
                buf.push(QUOTE_RE.exec(lines[i])[1]);
                i++;
            }
            blocks.push({ type: "quote", content: buf.join(" ") });
            continue;
        }
        if (line.trim() === "") {
            i++;
            continue;
        }
        // Paragraph: greedy until blank line or block-starting line.
        const buf = [line];
        i++;
        while (
            i < lines.length &&
            lines[i].trim() !== "" &&
            !lines[i].startsWith("```") &&
            !HEADING_RE.test(lines[i]) &&
            !RULE_RE.test(lines[i]) &&
            !BULLET_RE.test(lines[i]) &&
            !ORDERED_RE.test(lines[i]) &&
            !QUOTE_RE.test(lines[i]) &&
            // Stop before a table — header row plus a separator on the
            // next line — so paragraph doesn't gobble the table when
            // the model omits a blank line above it.
            !(
                TABLE_ROW_RE.test(lines[i]) &&
                i + 1 < lines.length &&
                TABLE_SEP_RE.test(lines[i + 1])
            )
        ) {
            buf.push(lines[i]);
            i++;
        }
        blocks.push({ type: "paragraph", content: buf.join(" ") });
    }
    return blocks;
}

// Single-pass inline tokenizer over a string. Returns an array of
// React nodes (strings + elements). Recurses for content inside
// emphasis/bold so nesting works (e.g. **bold *italic***).
// Exported for line-break-faithful consumers (the memory lens) that want inline
// formatting + link resolution WITHOUT the block parser's paragraph collapsing —
// they render it inside a `whitespace-pre-wrap` container so the source's own
// line breaks survive. `opts` is the optional host hook bag: `{ renderLink, wikiLinks }`. When
// absent (the default — chat, kernels, everywhere), external destinations use
// the shared laundering + disclosure link. `renderLink({ href, children, key })` (and, when `wikiLinks` is on,
// `{ wiki: true, target, children, key }`) lets a host override link rendering —
// the memory lens uses it to turn `[..](slug.md)` pointers and `[[slug]]`
// wikilinks into in-app navigation. Returning null/undefined falls back to the
// default external link (or literal text for an unhandled wikilink).
//
// `linkifyText({ text, key })` may replace a run of PLAIN text with nodes - the
// chat turns ids it recognises into in-app navigation. Called on prose runs and
// inline-code contents; never on fenced code, hrefs, or link labels. Return
// null to keep the original string.
export function renderInline(text, opts = {}) {
    const out = [];
    let plain = "";
    let i = 0;
    // Stripped for LABEL recursion only: an affordance inside an anchor is
    // invalid markup and gives one piece of text two destinations.
    let labelOpts = null;
    const forLabel = () => (labelOpts ||= (opts.linkifyText ? { ...opts, linkifyText: null } : opts));
    const flush = () => {
        if (!plain) return;
        const replaced = opts.linkifyText?.({ text: plain, key: `lx${out.length}` });
        out.push(replaced == null ? plain : replaced);
        plain = "";
    };
    while (i < text.length) {
        const c = text[i];

        if (c === "`") {
            const end = text.indexOf("`", i + 1);
            if (end !== -1) {
                flush();
                out.push(
                    <code
                        key={out.length}
                        className="rounded-[2px] bg-[color:var(--control-bg)] px-[4px] py-[1px] text-[0.88em] text-[color:var(--ink)]"
                    >
                        {(() => {
                            const code = text.slice(i + 1, end);
                            const replaced = opts.linkifyText?.({ text: code, key: "lx" });
                            return replaced == null ? code : replaced;
                        })()}
                    </code>,
                );
                i = end + 1;
                continue;
            }
        }
        if (c === "*" && text[i + 1] === "*") {
            const end = text.indexOf("**", i + 2);
            if (end !== -1) {
                flush();
                out.push(<strong key={out.length}>{renderInline(text.slice(i + 2, end), opts)}</strong>);
                i = end + 2;
                continue;
            }
        }
        if (c === "*") {
            const end = text.indexOf("*", i + 1);
            if (end !== -1 && text[end - 1] !== "*") {
                flush();
                // Emphasis renders as medium weight, never italic — the
                // dashboard does not use italics anywhere.
                out.push(<em key={out.length} className="not-italic font-medium">{renderInline(text.slice(i + 1, end), opts)}</em>);
                i = end + 1;
                continue;
            }
        }
        // Wikilink [[slug]] / [[slug|Display]] — only when the host opts in
        // (memory bodies). Routed through renderLink so the host owns
        // resolution; falls back to literal text when unhandled. Checked before
        // the single-`[` link branch since both start with `[`.
        if (opts.wikiLinks && c === "[" && text[i + 1] === "[") {
            const end = text.indexOf("]]", i + 2);
            if (end !== -1) {
                const inner = text.slice(i + 2, end);
                const bar = inner.indexOf("|");
                const target = (bar === -1 ? inner : inner.slice(0, bar)).trim();
                const labelText = bar === -1 ? inner : inner.slice(bar + 1);
                flush();
                const node = opts.renderLink
                    ? opts.renderLink({ wiki: true, target, children: renderInline(labelText, forLabel()), key: out.length })
                    : null;
                out.push(node == null ? `[[${inner}]]` : node);
                i = end + 2;
                continue;
            }
        }
        if (c === "[") {
            const close = text.indexOf("]", i + 1);
            if (close !== -1 && text[close + 1] === "(") {
                const urlEnd = text.indexOf(")", close + 2);
                if (urlEnd !== -1) {
                    const href = text.slice(close + 2, urlEnd);
                    const label = renderInline(text.slice(i + 1, close), forLabel());
                    flush();
                    let node = opts.renderLink ? opts.renderLink({ href, children: label, key: out.length }) : null;
                    if (node == null) {
                        node = (
                            <SafeRenderLink
                                key={out.length}
                                href={sanitizeRenderHref(href)}
                                trustedDocumentationHosts={opts.trustedDocumentationHosts}
                            >
                                {label}
                            </SafeRenderLink>
                        );
                    }
                    out.push(node);
                    i = urlEnd + 1;
                    continue;
                }
            }
        }

        plain += c;
        i++;
    }
    flush();
    return out;
}

const ALIGN_CLASS = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
};

function isNumericCell(text) {
    return /^\s*-?\$?\d[\d,]*(\.\d+)?\s*%?\s*$/.test(text || "");
}

function Block({ block, opts }) {
    switch (block.type) {
        case "table":
            return <MarkdownTable block={block} opts={opts} />;
        case "heading": {
            // Map # / ## / ### → typographic levels rather than literal h1/h2
            // (in a chat transcript an h1 would shout). Keep them all
            // semantically `<strong>` blocks at slightly different sizes.
            const cls =
                block.level <= 1
                    ? "mt-[6px] mb-[2px] text-[14px] font-semibold leading-[1.3]"
                    : block.level === 2
                      ? "mt-[6px] mb-[2px] text-[13px] font-semibold leading-[1.3]"
                      : "mt-[4px] mb-[2px] text-[12px] font-medium leading-[1.3]";
            return <div className={cls}>{renderInline(block.content, opts)}</div>;
        }
        case "code":
            return (
                <pre className="my-[6px] overflow-x-auto rounded-[2px] bg-[color:var(--control-bg)] px-[10px] py-[8px] text-[11.5px] leading-[1.5] text-[color:var(--ink)]">
                    <code>{block.content}</code>
                </pre>
            );
        case "list": {
            const Tag = block.ordered ? "ol" : "ul";
            const cls = block.ordered ? "list-decimal" : "list-disc";
            return (
                <Tag className={`${cls} my-[2px] pl-[18px] [&>li]:my-[1px]`} start={block.ordered && block.start !== 1 ? block.start : undefined}>
                    {block.items.map((item, idx) => (
                        <li key={idx}>{renderInline(item, opts)}</li>
                    ))}
                </Tag>
            );
        }
        case "rule":
            // A break between sections, as quiet as the transcript's own.
            return <hr className="my-[10px] h-0 border-0 border-t border-[color:var(--line-soft)]" />;
        case "quote":
            return (
                <blockquote className="my-[4px] border-l-[2px] border-[color:var(--line-strong)] pl-[8px] text-[color:var(--muted)]">
                    {renderInline(block.content, opts)}
                </blockquote>
            );
        case "paragraph":
        default:
            return <p className="my-[2px]">{renderInline(block.content, opts)}</p>;
    }
}

// Markdown tables route through the shared DataTable component so chat
// tables match the rest of the dashboard's tabular surfaces visually
// (header band, hover treatment, row borders). The columns are all
// flex-stretch so a 3-col table fills its container with equal tracks
// — short tables don't horizontal-scroll, long ones still do via
// DataTable's internal handling.
//
// `aligns` from the GFM separator (`:---`, `:---:`, `---:`) is honoured
// per cell; when the column doesn't specify one, numeric content
// right-aligns and prose left-aligns by default.
function MarkdownTable({ block, opts }) {
    const { header, rows, aligns } = block;
    const columns = useMemo(
        () =>
            header.map((label, i) => ({
                id: String(i),
                label,
                width: 120,
                flex: true,
            })),
        [header],
    );
    const data = useMemo(
        () =>
            rows.map((row) =>
                Object.fromEntries(row.map((v, j) => [String(j), v ?? ""])),
            ),
        [rows],
    );
    const cols = useDataTableColumns({
        columns,
        defaultVisible: columns.map((c) => c.id),
    });
    // Everything stays left-aligned. `tabular-nums` keeps digit
    // columns visually crisp without right-aligning them (which made
    // numeric columns read as a separate strip from the rest of the
    // row). GFM `:---:` separator alignment is intentionally ignored
    // for the same reason.
    const renderCell = useCallback((row, col) => {
        const text = row[col.id] ?? "";
        const numeric = isNumericCell(text);
        return (
            <div
                className={[
                    "min-w-0 truncate text-[11px] leading-[1.4] text-left",
                    numeric ? "tabular-nums" : "",
                ].join(" ")}
                title={String(text)}
            >
                {renderInline(String(text), opts)}
            </div>
        );
    }, [opts]);

    if (!header.length) return null;

    // No `maxHeight` — see the matching note on AnalyticsResultTable.
    // Markdown tables flow into the chat's outer scroll naturally so
    // the chat stays a single Y-scroll surface; horizontal scroll
    // still kicks in for wide tables via DataTable's overflow-auto.
    return (
        <ExportableBlock filenamePrefix="md-table" className="my-[6px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)]">
            <DataTable
                cols={cols}
                rows={data}
                getRowId={(_, i) => String(i)}
                renderCell={renderCell}
                estimatedRowHeight={26}
            />
        </ExportableBlock>
    );
}

function MarkdownInner({
    text,
    renderLink,
    linkifyText,
    wikiLinks = false,
    trustedDocumentationHosts,
}) {
    const blocks = useMemo(() => parseBlocks(text || ""), [text]);
    const opts = useMemo(
        () => ({ renderLink, linkifyText, wikiLinks, trustedDocumentationHosts }),
        [renderLink, linkifyText, wikiLinks, trustedDocumentationHosts],
    );
    return (
        <div className="[overflow-wrap:anywhere] [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            {blocks.map((b, i) => (
                <Block key={i} block={b} opts={opts} />
            ))}
        </div>
    );
}

const Markdown = memo(MarkdownInner);
export default Markdown;
